import { useEffect, useState } from 'react';
import useUrlParams from '../../../../hooks/useUrlParams';
import useUrlNavigation from '../../../../hooks/useUrlNavigation';
import { get_navigator_button_by_dashboard } from '../Utils'
import { AngularLoadingSpinner, FloatingDownArrowSvg, OnboardFolderIcon, UploadFileSvg, UploadSuccessTick } from '../Svgs';
import AllFiles from './AllFiles';
import UploadDownloadQueue from '../UploadDownloadQueue';
import CustomFileUploadBtn from '../../../../custom_components/CustomFileUploadBtn';
import CustomToolTip from '../../../../../CommonComponents/CustomToolTip';
import useFiles from '../../../../hooks/useFiles';
import CustomFilter from '../../../../../CommonComponents/custom_filter/CustomFilter';
import { formatFileSize } from '../../../../../../utils/commonFunctions';
import './GroundFiles.scss'
import './../Files.scss'
import useScenario from '../../../../hooks/useScenario';
import { isSummaryMode } from '../../../../hooks/utils';

type UploadingFile = {
    id: string;
    fileName: string;
    fileSize: string;
    status: string;
};


const GroundFiles = ({ setTypeOfAction, setActionFileId, isReadOnlyActions = false }) => {
    const { navigate_to } = useUrlNavigation()
    const { tab_name, dashboard } = useUrlParams()
    const { scenario } = useScenario()
    const summaryMode = isSummaryMode(dashboard, scenario)
    const { postFileToMinio, allACPStorageFiles } = useFiles();
    const [allFilters, setAllFilters] = useState({})
    const [selectedPayload, setSelectedPayload] = useState<string | null>(null)
    const [filteredGroundFiles, setFilteredGroundFiles] = useState<any[] | null>(null); //files present in minio based on search
    const [uploadingMinioFiles, setUploadingMinioFiles] = useState<UploadingFile[]>([]) //files which are being upload to minio

    // SET THE INITIAL FOLDER AND PERSIST SEARCH FIELD
    useEffect(() => {
        if (!allACPStorageFiles) return;

        const folderKeys = Object.keys(allACPStorageFiles);

        // If no payload selected, pick the FIRST folder
        if (!selectedPayload) {
            if (folderKeys.length > 0) {
                const firstKey = folderKeys[0];
                setSelectedPayload(firstKey);

                const firstFolder = allACPStorageFiles[firstKey];
                setFilteredGroundFiles(Array.from(firstFolder.values()));
            }
            return; // stop here until selectedPayload updates
        }

        // When payload already selected → apply search & filtering
        if (allACPStorageFiles[selectedPayload]) {
            const searchTerm = allFilters["searchFilter"] || "";
            const filteredArray = filteredFiles(searchTerm, selectedPayload);
            setFilteredGroundFiles(filteredArray);
        }
    }, [allACPStorageFiles, selectedPayload, allFilters]);


    //GET UPLOADING FILES TO MINION
    useEffect(() => {
        const files = JSON.parse(localStorage.getItem('uploadedFiles') || '[]')
        const element = document.querySelector('.floating-window-for-status');
        if (files?.length > 0 && element) element.classList.add('expand');
        setUploadingMinioFiles(files)
    }, [localStorage.getItem('uploadedFiles')])

    //FILTER FILES WHEN SEARCHING
    const filteredFiles = (name = '', payloadKey?: string) => {
        const search = name.toLowerCase();
        const selectedKey = payloadKey || selectedPayload;
        if (!allACPStorageFiles || !selectedKey) return [];

        const selectedMap = allACPStorageFiles[selectedKey];
        if (!selectedMap) return [];
        const files = Array.from(selectedMap.values()) as any[];

        return files?.filter(file => {
            const fileName = file.fileName?.toLowerCase() || '';
            const lastModified = file.lastModified?.toLowerCase() || '';
            const fileSizeFormatted = formatFileSize(file.fileSize || 0).toLowerCase() || '';

            // MAP RAW STATUS TO DISPLAY STATUS
            let displayStatus = '';
            if (file.fileStatus === 'Suspend Inprogress') displayStatus = 'pausing';
            else if (file.fileStatus === 'Resume Inprogress') displayStatus = 'resuming';
            else if (file.fileStatus === 'Suspended') displayStatus = 'paused';
            else if (file.fileStatus === 'Pending') displayStatus = '';
            else displayStatus = file.fileStatus?.toLowerCase() || '';

            const nameMatch = fileName.includes(search);
            const dateMatch = lastModified.includes(search);
            const statusMatch = displayStatus.includes(search);
            const fileSizeMatch = fileSizeFormatted.includes(search);
            return nameMatch || dateMatch || statusMatch || fileSizeMatch;
        });
    };

    //HANDLE FILTER OF GROUND FILES
    const handleGroundFilesFilters = (filters) => {
        const result = filteredFiles(filters?.searchFilter || '');
        setFilteredGroundFiles(result);
    }

    //HANDLE OPENING AND CLOSING OF FLOAT MENU
    const handleOpenFloating = () => {
        const element = document.querySelector('.floating-window-for-status');
        if (element) {
            element.classList.toggle('expand');
        }
    };

    //HANDLE GROUND-ONBOARD-DOWNLOAD FILES NAVIGATION
    const handleFileTabNavigation = (e, btn) => {
        e.stopPropagation()
        navigate_to(btn.name)
    }

    //HANDLE FOLDER CLICK
    const handleFolderClick = (payloadKey: string) => {
        if (payloadKey === selectedPayload) return;
        setSelectedPayload(payloadKey);
        const searchTerm = allFilters['searchFilter'] || '';
        const filteredArray = filteredFiles(searchTerm, payloadKey); // pass payloadKey explicitly
        setFilteredGroundFiles(filteredArray);
    };

    return (
        <>
            <div className='file__container__body'>
                <div className='ground__files__contact__container'>
                    <div className='d-flex gap-3 w-100'>
                        {/* LEFT SECTION */}
                        <div className='ground__files__container__left__container__wrapper'>
                            <div className='ground__files__container__header__container'>
                                {/* NAVIGATION BUTTONS */}
                                <div className='ground__files__container__header__left__container'>
                                    {get_navigator_button_by_dashboard(dashboard!).map((btn) => {
                                        return (
                                            <button key={btn.name} className={`button ${tab_name === btn.name && 'active'}`} onClick={(e) => { handleFileTabNavigation(e, btn) }}>{btn.label}</button>
                                        )
                                    })}
                                </div>

                                {/* HEADER BUTTONS CONTAINER */}
                                <div className='ground__files__container__header__right__container'>
                                    {/* FILTER COMPONENT */}
                                    <CustomFilter
                                        alignment='right'
                                        search={{
                                            applyFilter: (filters: { [key: string]: any }) => {
                                                handleGroundFilesFilters(filters)
                                            }
                                        }}
                                        allFilters={allFilters}
                                        setAllFilters={setAllFilters}
                                        filterCase={'file'}
                                        boundingScreen={'ground__files__container__left__container__wrapper'}
                                    />
                                    {/* UPLOAD BUTTON */}
                                    {!summaryMode && selectedPayload !== 'downloads' && (
                                        <CustomToolTip title='Upload File' placement='top'>
                                            <CustomFileUploadBtn icon={<UploadFileSvg />} postFileToMinio={postFileToMinio} />
                                        </CustomToolTip>
                                    )}
                                </div>
                            </div>
                            <div className='ground__files__explorer__body'>
                                {/* PAYLOAD FOLDERS */}

                                <div className='onboard__folder__container'>
                                    <div className='onboard__folder__container__body'>
                                        {allACPStorageFiles &&
                                            Object.keys(allACPStorageFiles).map((payloadKey) => (
                                                <div
                                                    key={payloadKey}
                                                    className={`onboard__folder__container__body__row cursor-pointer ${payloadKey === selectedPayload ? 'active' : ''}`}
                                                    onClick={() => handleFolderClick(payloadKey)}
                                                >
                                                    <div className='folder-icon'>
                                                        <OnboardFolderIcon />
                                                    </div>
                                                    <span className='folder-name'>{payloadKey}</span>
                                                </div>
                                            ))}
                                    </div>
                                </div>

                                {/* LIST OF ALL FILES */}
                                <AllFiles isReadOnlyActions={isReadOnlyActions} type={'ground'} allFiles={filteredGroundFiles} setTypeOfAction={setTypeOfAction} setActionFileId={setActionFileId} selectedPayload={selectedPayload} />

                            </div>
                            {/* FLOATING CARD FOR UPLOADING MINIO FILES */}
                            <div className={`floating-window-for-status ${uploadingMinioFiles?.length > 0 ? '' : 'hidden'} expand`}>
                                <div className='status-header-floating'>
                                    <div className='left-section'>Adding Files</div>
                                    <div className='right-section-icons'>
                                        <span className='opacity-50'>{(uploadingMinioFiles?.filter(f => f.status === 'uploading')?.length ?? 0)}/{uploadingMinioFiles?.length ?? 0}</span>
                                        <span className='arrow-icon' onClick={handleOpenFloating}><FloatingDownArrowSvg /></span>
                                    </div>
                                </div>
                                <div className='status-body-floating'>
                                    {uploadingMinioFiles?.length > 0 && uploadingMinioFiles?.map((file) => {
                                        return (
                                            <div className='status-body-floating-item' key={file?.id}>
                                                <div className='left-section'>
                                                    <span className='file-name-in-uploading text-truncate' title={file?.fileName}>{file?.fileName}</span>
                                                    <span className='file-size-in-uploading'>{formatFileSize(file?.fileSize)}</span>
                                                </div>
                                                <div className='right-section'>{file?.status === 'uploading' ? <AngularLoadingSpinner /> : <UploadSuccessTick />}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* UPLOAD DOWNLOAD QUEUE */}
                        <UploadDownloadQueue isReadOnlyActions={isReadOnlyActions} type={'ground'} setTypeOfAction={setTypeOfAction} setActionFileId={setActionFileId} />
                    </div>
                </div>
            </div >
        </>
    )
}

export default GroundFiles;